// THEME_BUNDLES — the deck-level theme catalogue. A bundle ties a colour scheme
// (the tokens.css [data-theme] value) to a default fontset, accent ink and
// default background, so picking a bundle re-themes the whole deck coherently —
// while each axis (theme / font / accent / background) stays individually
// overridable (the "hybrid" model). One source of truth for the allow-lists
// that feed safeTheme(), the Studio pickers and (Phase 6) the Go AI contract.

import type { BgType, FontAxis } from './slide-model';

export interface ThemeBundle {
  id: string;          // the data-theme value (also the bundle id)
  label: string;       // human label for the picker
  blurb: string;       // one line for the picker / AI
  fontset: FontAxis;   // default font axis for the bundle
  accent: string;      // default brand ink (hex) — the sanctioned per-deck colour
  defaultBackground: BgType; // the background new slides lean on in this bundle
  dark: boolean;       // dark scheme (for the picker swatch + AI guidance)
}

export const THEME_BUNDLES: ThemeBundle[] = [
  { id: 'workplace-paper', label: 'Paper', blurb: 'Warm light paper — the house style.', fontset: 'editorial', accent: '#00A76F', defaultBackground: 'gradient', dark: false },
  { id: 'workplace-slate', label: 'Slate', blurb: 'Cool dark slate, calm and corporate.', fontset: 'inter', accent: '#2C5470', defaultBackground: 'gradient', dark: true },
  { id: 'warm', label: 'Warm', blurb: 'Warm editorial — clay accents, serif display.', fontset: 'editorial', accent: '#B87333', defaultBackground: 'gradient', dark: false },
  { id: 'light', label: 'Light', blurb: 'Clean neutral light, minimal chrome.', fontset: 'inter', accent: '#00A76F', defaultBackground: 'none', dark: false },
  { id: 'pure', label: 'Pure', blurb: 'High-contrast monochrome on white.', fontset: 'inter', accent: '#1E2A3A', defaultBackground: 'none', dark: false },
  { id: 'dark', label: 'Dark', blurb: 'Neutral dark, content-forward.', fontset: 'inter', accent: '#2C5470', defaultBackground: 'gradient', dark: true },
  { id: 'midnight', label: 'Midnight', blurb: 'Deep blue-black, premium and quiet.', fontset: 'inter', accent: '#5E7E9C', defaultBackground: 'gradient', dark: true },
  { id: 'black', label: 'Black', blurb: 'True black, OLED-ready, maximal contrast.', fontset: 'inter', accent: '#00A76F', defaultBackground: 'none', dark: true },
];

/** Allow-listed theme/scheme ids (the data-theme values). */
export const THEME_IDS: readonly string[] = THEME_BUNDLES.map((b) => b.id);

/** The two loaded font voices. Graphik/Museo land here once self-hosted. */
export const FONT_AXES: { id: FontAxis; label: string; blurb: string }[] = [
  { id: 'inter', label: 'Inter', blurb: 'Clean grotesque — sans throughout.' },
  { id: 'editorial', label: 'Editorial', blurb: 'Fraunces serif display over Inter body.' },
];

/** The bundle for an id, defaulting to Paper (the house style). */
export function bundleFor(id?: string): ThemeBundle {
  return THEME_BUNDLES.find((b) => b.id === id) || THEME_BUNDLES[0];
}
